import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  SupportedChainId,
  TransactionInfo,
  TxStatus,
} from '@orionprotocol/sdk';

import { Contracts } from '../../types';

export type Tx = TransactionInfo & {
  id: string;
  datetime: number;
  summary: string;
  chainId: SupportedChainId;
  from: string;
  to: string | undefined | null;
  data: string | undefined | null;
  gasLimit?: string | number;
  gasPrice?: string | number;
  value?: string;
  confirmations?: number;
  isReversedOrder?: boolean;
  contract?: Contracts;
};

type TransactionsState = {
  transactions: Tx[];
  transactionInSigningId: string | null;
};

const initialState: TransactionsState = {
  transactions: [],
  transactionInSigningId: null,
};

export const transactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    addTransactionToQueue(state, action: PayloadAction<Tx>) {
      state.transactions.push({
        ...action.payload,
        status: TxStatus.QUEUED,
      });
    },
    updateTransaction(
      state,
      action: PayloadAction<{
        id: string;
        params: Partial<Tx>;
      }>,
    ) {
      const foundTransactionIndex = state.transactions.findIndex(
        (tx) => tx.id === action.payload.id,
      );
      if (foundTransactionIndex !== -1) {
        state.transactions[foundTransactionIndex] = {
          ...state.transactions[foundTransactionIndex],
          ...action.payload.params,
        };
      }
    },
    clearAll(
      state,
      action: PayloadAction<{
        user: string;
        chain: SupportedChainId;
      }>,
    ) {
      state.transactions = state.transactions.filter(
        ({ chainId, from }) =>
          !(chainId === action.payload.chain && from === action.payload.user),
      );
    },
    setTransactionInSigningId(state, action: PayloadAction<string | null>) {
      state.transactionInSigningId = action.payload;
    },
    removeOldest(
      state,
      action: PayloadAction<{
        user: string;
        chain: SupportedChainId;
      }>,
    ) {
      const oldest = state.transactions.find(
        ({ chainId, from }) =>
          chainId === action.payload.chain && from === action.payload.user,
      );
      state.transactions = state.transactions.filter((elem) => elem !== oldest);
    },
    clearFailedTransactions(
      state,
      action: PayloadAction<{
        user: string;
        chain: SupportedChainId;
      }>,
    ) {
      state.transactions = state.transactions.filter(
        ({ chainId, from, status }) =>
          status === TxStatus.FAILED &&
          !(chainId === action.payload.chain && from === action.payload.user),
      );
    },
    removeLastSignFailedTx(state) {
      let updatedState = state.transactions;
      const lastFailedTx = [...updatedState]
        .reverse()
        .find(({ status }) => status === TxStatus.SIGN_FAILED);
      updatedState = updatedState.filter((tx) => tx.id !== lastFailedTx?.id);

      state.transactions = updatedState;
    },
  },
});

export const transactionsReducer = transactionsSlice.reducer;
